<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <nx-editor>
    <template v-slot:title>
      <v-icon
        slot="activator"
        class="nx-ui--level-1 nx-negative px-2"
      >
        today
      </v-icon>
      <span class="nx-title">{{ block.alias | capitalize }}</span>
    </template>

    <template #metadata>
      <nx-chip
        class="hidden-sm-and-down"
        dark
        info
      >
        ID: {{ blockId }}
      </nx-chip>

      <v-tooltip
        v-if="block.creationDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ block.creationDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.creationDate') }}
      </v-tooltip>

      <v-tooltip
        v-if="block.modificationDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ block.modificationDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.modificationDate') }}
      </v-tooltip>

      <v-tooltip
        v-if="block.publishedDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ block.publishedDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.publishedDate') }}
      </v-tooltip>
    </template>

    <template #actions />

    <template #extension>
      <nx-editor-breadcrumbs
        :id="refBoard"
        type="dayparting"
        :block-id="blockId"
      />
      <v-layout
        row
        class="fab-container"
      >
        <nx-round-button
          v-if="dirty"
          :disabled="disabled || blocked"
          :loading="loading"
          color="accent"
          icon="save"
          @click.native="save"
        />
      </v-layout>
    </template>

    <template #body>
      <nx-dayparting-calendar
        :rows="rows"
        :disabled="blocked"
        :ref-board="refBoard"
        :block-id="blockId"
      />
      <nx-dialog-confirm
        ref="confirm"
        :text="$t('message.content.editor.unsavedChangesConfirmText')"
        :title="$t('message.content.editor.unsavedChanges')"
        :accept-text="$t('message.content.editor.unsavedChangesAcceptText')"
        :cancel-text="$t('message.content.editor.unsavedChangesCancelText')"
        accept-color="primary"
        cancel-color="primary"
      />
    </template>
  </nx-editor>
</template>

<script>
import NxRoundButton from '@/components/buttons/RoundButton';
import NxEditor from '@/components/editor/Editor';
import loadable from '@/mixins/loadable';
import BasePage from '@/pages/BasePage';
import NxDaypartingCalendar from '@/modules/board/dayparting/components/Calendar';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import toast from '@/modules/toast/services';
import blockable from '@/modules/block/mixins/blockable';
import { mapState } from 'vuex';
import NxEditorBreadcrumbs from '@/components/editor/EditorBreadcrumbs';
import { SiteMismatchError } from '@/api/interceptors/format-error';
import NxChip from '@/components/chips/Chip';

export default {
  name: 'NxDaypartingEditor',
  components: {
    NxChip,
    NxEditorBreadcrumbs,
    NxDialogConfirm,
    NxDaypartingCalendar,
    NxRoundButton,
    NxEditor,
  },
  extends: BasePage,
  mixins: [loadable, blockable],
  metaInfo() {
    return {
      title: this.block ? this.block.title : null,
      // all titles will be injected into this template
      titleTemplate: this.block ? '%s | BCube CMS' : 'BCube CMS',
    };
  },
  props: {
    blockId: {
      type: Number,
      required: true,
    },
    refBoard: Number,
  },
  data() {
    return {
      dayparting: {},
      disabled: false,
    };
  },
  computed: {
    ...mapState('dayparting', ['draft', 'master', 'dirty']),
    block() {
      return this.master;
    },
    rows() {
      return this.draft.rows || [];
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch('dayparting/INIT_EDITOR', this.blockId);
    } catch (err) {
      console.error(err);
      if (err instanceof SiteMismatchError) {
        this.$router.push({
          name: 'siteMismatch',
          params: { ...err.cmsVariables, path: this.$route.path },
        });
        return;
      }
      this.$router.push({ name: 'notFound', path: this.$route.path });
    } finally {
      this.finishLoading();
    }
  },
  methods: {
    async save() {
      if (this.loading) {
        return;
      }
      try {
        this.loadingStart();
        await this.$store.dispatch('dayparting/SAVE', this.blockId);
        toast.notify('message.entity.actions.savedSuccess');
      } catch (err) {
        console.error(err);
        toast.error('message.entity.actions.savedFailed');
      } finally {
        this.loadingFinish();
      }
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.dirty) {
      return next();
    }
    // Unsaved changes
    const agree = await this.$refs.confirm.open();
    if (agree) {
      return next();
    }
    return next(false);
  },
};
</script>

<style lang="stylus" scoped>
.fab-container {
  position: absolute;
  bottom: -32px;
  right: 0;
}
</style>
